<div class="df-page">
  <app-project-nav></app-project-nav>
  <app-page-header
    class="df-head"
    size="compact"
    [title]="'defect.list.pageTitle' | translate"
    [subtitle]="'defect.list.pageSubtitle' | translate"
  >
    <ng-container right>
      <app-stat-group [items]="stats()" />
    </ng-container>
  </app-page-header>

  <div class="df-toolbar">
    <app-search-bar
      class="df-search"
      data-testid="defects-search"
      [placeholder]="'defect.list.searchPlaceholder' | translate"
      [value]="query()"
      (valueChange)="onQuery($event)"
    />
    <select
      class="df-select"
      data-testid="filter-dropdown-status"
      [class.df-select--active]="!!statusFilter()"
      [attr.aria-label]="'defect.list.filter.statusAria' | translate"
      (change)="onStatusFilter($any($event.target).value)"
    >
      <option value="" [selected]="statusFilter() === ''">{{ 'defect.list.filter.status' | translate }}</option>
      @for (opt of statusOptions(); track opt.value) {
        <option [value]="opt.value" [selected]="opt.value === statusFilter()">{{ opt.label }}</option>
      }
    </select>
    <select
      class="df-select"
      data-testid="filter-dropdown-release"
      [class.df-select--active]="!!releaseFilter()"
      [attr.aria-label]="'defect.list.filter.releaseAria' | translate"
      (change)="onReleaseFilter($any($event.target).value)"
    >
      <option value="" [selected]="releaseFilter() === ''">{{ 'defect.list.filter.release' | translate }}</option>
      @for (opt of releaseOptions(); track opt.value) {
        <option [value]="opt.value" [selected]="opt.value === releaseFilter()">{{ opt.label }}</option>
      }
    </select>
    <select
      class="df-select"
      data-testid="filter-dropdown-assignee"
      [class.df-select--active]="!!assigneeFilter()"
      [attr.aria-label]="'defect.list.filter.assigneeAria' | translate"
      (change)="onAssigneeFilter($any($event.target).value)"
    >
      <option value="" [selected]="assigneeFilter() === ''">{{ 'defect.list.filter.assignee' | translate }}</option>
      @for (opt of assigneeOptions(); track opt.value) {
        <option [value]="opt.value" [selected]="opt.value === assigneeFilter()">{{ opt.label }}</option>
      }
    </select>
    <app-saved-views-menu
      data-testid="defects-saved-views"
      saveHeadingKey="savedViews.saveAsNew"
      [views]="savedViewOptions()"
      [activeName]="activeViewName()"
      [context]="'DEFECTS'"
      [projectId]="projectId"
      [updatable]="true"
      [activeDirty]="activeViewDirty()"
      (viewSelected)="onViewSelected($event)"
      (viewSaved)="saveView($event)"
      (viewUpdated)="saveView($event)"
      (viewDeleted)="onViewDeleted($event)"
      (viewDeleteFailed)="onViewDeleteFailed()"
    />
    @if (hasFilter()) {
      <app-reset-filter-button [label]="'defect.list.filter.reset' | translate" (resetClick)="resetFilters()" />
    }
  </div>

  @if (viewsErrorKey(); as errorKey) {
    <p class="df-views-error" role="alert" data-testid="defects-views-error">{{ errorKey | translate }}</p>
  }

  <div class="df-card">
    <div class="df-card-head">
      <span class="df-card-title" data-testid="defects-card-title">{{ 'defect.list.tableLabel' | translate }}</span>
    </div>
    <div class="df-table" role="table" [attr.aria-label]="'defect.list.tableLabel' | translate">
      <div class="df-table-header" role="row">
        <div class="df-col df-col--key" role="columnheader">{{ 'defect.list.column.key' | translate }}</div>
        <div class="df-col df-col--title" role="columnheader">{{ 'defect.list.column.title' | translate }}</div>
        <div class="df-col df-col--severity" role="columnheader">{{ 'defect.list.column.severity' | translate }}</div>
        <div class="df-col df-col--status" role="columnheader">{{ 'defect.list.column.status' | translate }}</div>
        <div class="df-col df-col--assignee" role="columnheader">{{ 'defect.list.column.assignee' | translate }}</div>
        <div class="df-col df-col--ref" role="columnheader">{{ 'defect.list.column.testCase' | translate }}</div>
        <div class="df-col df-col--ref" role="columnheader">{{ 'defect.list.column.testRun' | translate }}</div>
      </div>
      @if (loading()) {
        <div class="df-state" data-testid="defects-loading">
          <span class="df-spinner" aria-hidden="true"></span>
          <p>{{ 'defect.list.loading' | translate }}</p>
        </div>
      } @else if (loadError()) {
        <div class="df-state" data-testid="defects-error">
          <p class="df-state__error" role="alert">{{ 'defect.list.loadError' | translate }}</p>
          <button type="button" class="df-secondary-btn" data-testid="defects-retry" (click)="load()">
            <span aria-hidden="true">↻</span>
            {{ 'defect.list.retry' | translate }}
          </button>
        </div>
      } @else if (defects().length === 0) {
        <div class="df-state" data-testid="defects-empty">
          <p>{{ (hasFilter() ? 'defect.list.emptyFiltered' : 'defect.list.empty') | translate }}</p>
          @if (hasFilter()) {
            <button type="button" class="df-secondary-btn" data-testid="defects-reset-empty" (click)="resetFilters()">
              {{ 'defect.list.filter.reset' | translate }}
            </button>
          }
        </div>
      } @else {
        @for (defect of defects(); track defect.id) {
          <div
            class="df-row"
            role="row"
            tabindex="0"
            data-testid="defect-row"
            [attr.aria-label]="defect.key + ' ' + defect.title"
            (click)="open(defect)"
            (keydown.enter)="open(defect)"
            (keydown.space)="$event.preventDefault(); open(defect)"
          >
            <div class="df-col df-col--key" role="cell">
              <code class="df-code">{{ defect.key }}</code>
            </div>
            <div class="df-col df-col--title" role="cell">
              <span class="df-title">{{ defect.title }}</span>
              @if (defect.markedBlocking) {
                <span class="df-blocking-badge" [attr.title]="'defect.list.blockingTooltip' | translate">
                  {{ 'defect.list.blocking' | translate }}
                </span>
              }
            </div>
            <div class="df-col df-col--severity" role="cell" data-testid="defect-severity">
              <span class="df-pill" [class]="'df-pill--' + severityTone(defect.severity)">
                <span class="df-pill__dot" aria-hidden="true"></span>
                {{ 'defect.severity.' + defect.severity | translate }}
              </span>
            </div>
            <div class="df-col df-col--status" role="cell" data-testid="defect-status">
              <span class="df-pill" [class]="'df-pill--' + statusTone(defect.status)">
                <span class="df-pill__dot" aria-hidden="true"></span>
                {{ 'defect.status.' + defect.status | translate }}
              </span>
            </div>
            <div class="df-col df-col--assignee" role="cell" data-testid="defect-assignee">
              @if (defect.assignee; as assignee) {
                @let unbekannt = !assignee.name;
                @let name = assignee.name || ('common.unknownUser' | translate);
                <app-avatar
                  aria-hidden="true"
                  [name]="name"
                  [size]="24"
                  [neutral]="unbekannt"
                  [initialen]="unbekannt ? '?' : null"
                />
                <span class="df-assignee-name">{{ name }}</span>
              } @else {
                <span class="df-unassigned">{{ 'defect.list.unassigned' | translate }}</span>
              }
            </div>
            <div class="df-col df-col--ref" role="cell">
              @if (defect.testCaseKey) {
                <code class="df-code">{{ defect.testCaseKey }}</code>
              } @else {
                <span class="df-missing" data-testid="defect-missing">–</span>
              }
            </div>
            <div class="df-col df-col--ref" role="cell">
              @if (defect.testRunKey) {
                <code class="df-code">{{ defect.testRunKey }}</code>
              } @else {
                <span class="df-missing" data-testid="defect-missing">–</span>
              }
            </div>
          </div>
        }
      }
    </div>
    @if (hasRows()) {
      <div class="df-card-foot">
        <span>{{ 'defect.list.footer.rowsPerPage' | translate }}</span>
        <select
          class="df-page-size"
          data-testid="defects-page-size"
          [attr.aria-label]="'defect.list.footer.rowsPerPage' | translate"
          (change)="onPageSize(+$any($event.target).value)"
        >
          @for (size of pageSizeOptions; track size) {
            <option [value]="size" [selected]="pageSize() === size">{{ size }}</option>
          }
        </select>
        <span class="df-card-foot__spacer"></span>
        <span data-testid="defects-range">
          {{ 'defect.list.footer.range' | translate: { start: rangeStart(), end: rangeEnd(), total: totalElements() } }}
        </span>
        <div class="df-pager">
          <button
            type="button"
            class="df-pager__btn"
            data-testid="defects-prev"
            [disabled]="pageIndex() === 0"
            [attr.aria-label]="'defect.list.footer.previous' | translate"
            (click)="onPageIndex(pageIndex() - 1)"
          >
            <mat-icon aria-hidden="true">chevron_left</mat-icon>
          </button>
          <button
            type="button"
            class="df-pager__btn"
            data-testid="defects-next"
            [disabled]="pageIndex() >= pageCount() - 1"
            [attr.aria-label]="'defect.list.footer.next' | translate"
            (click)="onPageIndex(pageIndex() + 1)"
          >
            <mat-icon aria-hidden="true">chevron_right</mat-icon>
          </button>
        </div>
      </div>
    }
  </div>
</div>
